import React, { useState, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Tooltip } from 'primereact/tooltip';
import { saveAs } from 'file-saver';
import axios from 'axios';
import { TieredMenu } from 'primereact/tieredmenu';
import { Dialog } from 'primereact/dialog';
import { useMountEffect } from 'primereact/hooks';
import StorageHelper from '../../../utils/storageHelper';

import "primereact/resources/themes/saga-blue/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";

import ViewMainMemberTable from "./components/ViewMainMemberTable.jsx";
import ViewSpouseMemberTable from "./components/ViewSpouseMemberTable.jsx";
import ViewMemberBeneficiaries from "./components/ViewMemberBeneficiaries.jsx";
import ViewChildBeneficiaries from "./components/ViewChildBeneficiaries.jsx";
import ViewExtendedBeneficiaryDetails from "./components/ViewExtendedBeneficiaryDetails.jsx";
import ViewBankingDetails from "./components/ViewBankingDetails.jsx";

const ApprovePolicyTable = () => {
  const [Policies, setPolicies] = useState([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [activePolicy, setActivePolicy] = useState(null);
  const [dialogPolicyVisible, setPolicyDialogVisible] = useState(false);

  const dt = useRef(null);
  const menu = useRef(null);

  const cols = [
    { field: 'mainMember.ID_Number', header: 'National ID' },
    { field: 'productName', header: 'Product Name' },
    { field: 'applicationStatus', header: 'Status' },
    { field: 'open-policy', header: 'View Full Policy' }
  ];

  const exportColumns = cols
      .filter(col => col.field !== 'open-policy')
      .map((col) => ({ title: col.header, dataKey: col.field }));

  useMountEffect(() => {
    fetchPolicies();
  }, []);

  const fetchPolicies = async () => {
    try {
      const response = await axios.get(process.env.REACT_APP_DOVESAPI + 'policy/list/applications', {
        headers: {
          Authorization: 'Bearer ' + StorageHelper.get('token')
        }
      });

      if (response.data.status === "SUCCESS") {
        setPolicies(response.data.responseBody);
      }
    } catch (error) {
      console.error('Error fetching policies:', error);
    }
  };

  const renderOpenPolicyAction = (rowData) => {
    return (
        <Button
            raised
            text
            size="small"
            icon="pi pi-eye"
            className="p-button-info m-1"
            onClick={() => {
              setActivePolicy(rowData);
              setPolicyDialogVisible(true);
            }}
        >
          View More
        </Button>
    );
  };

  const exportCSV = (selectionOnly) => {
    dt.current.exportCSV({ selectionOnly });
  };

  const exportPdf = () => {
    import('jspdf').then((jsPDF) => {
      import('jspdf-autotable').then(() => {
        const doc = new jsPDF.default();
        doc.autoTable(exportColumns, Policies);
        doc.save('policy-applications-report.pdf');
      });
    });
  };

  const exportExcel = () => {
    import('xlsx').then((xlsx) => {
      const worksheet = xlsx.utils.json_to_sheet(Policies);
      const workbook = { Sheets: { data: worksheet }, SheetNames: ['data'] };
      const excelBuffer = xlsx.write(workbook, {
        bookType: 'xlsx',
        type: 'array'
      });
      saveAsExcelFile(excelBuffer, 'policy-applications-report');
    });
  };

  const saveAsExcelFile = (buffer, fileName) => {
    saveAs(new Blob([buffer], { type: 'application/octet-stream' }), fileName + '.xlsx');
  };

  const onGlobalFilter = (event) => {
    setGlobalFilter(event.target.value);
  };

  const globalFilterElement = (
      <input
          type="text"
          className="p-inputtext p-component"
          placeholder="Search"
          value={globalFilter}
          onChange={onGlobalFilter}
      />
  );

  const items = [
    {
      label: 'Download CSV',
      icon: 'pi pi-file-excel',
      command: () => { exportCSV(false) }
    },
    {
      label: 'Download PDF',
      icon: 'pi pi-file-pdf',
      command: () => { exportPdf() }
    },
    {
      label: 'Download Excel',
      icon: 'pi pi-file-excel',
      command: () => { exportExcel() }
    }
  ];

  return (
      <div className="card home-chart">
        <Dialog
            header="Full Policy Detail"
            visible={dialogPolicyVisible}
            maximizable
            style={{ width: '60vw' }}
            onHide={() => {
              setPolicyDialogVisible(false);
              setActivePolicy(null);
            }}
        >
          {activePolicy && (
              <>
                <ViewMainMemberTable activeUserData={activePolicy} />
                <ViewSpouseMemberTable activeUserData={activePolicy} />
                <ViewChildBeneficiaries activeUserData={activePolicy} />
                <ViewExtendedBeneficiaryDetails activeUserData={activePolicy} />
                <ViewMemberBeneficiaries activeUserData={activePolicy} />
                <ViewBankingDetails activeUserData={activePolicy} />
              </>
          )}
        </Dialog>

        <Tooltip target=".export-buttons>button" position="bottom" />
        <div className="card-header">
          <h2 className="card-title home-chart">Policy Applications</h2>
          <div className="flex justify-content-end">
          <span className="p-input-icon-left">
            <i className="pi pi-search" />
            {globalFilterElement}
          </span>
          </div>
          <div id="report-select">
            <TieredMenu model={items} popup ref={menu} breakpoint="767px" />
            <Button label="Download Report" icon="pi pi-download" onClick={(e) => menu.current.toggle(e)} />
          </div>
        </div>

        <div className="card-body">
          <DataTable
              paginator
              rows={5}
              rowsPerPageOptions={[5, 10, 25, 50]}
              ref={dt}
              value={Policies}
              tableStyle={{ minWidth: '50rem' }}
              globalFilter={globalFilter}
              emptyMessage="No Policies found"
          >
            {cols.map((col, index) => {
              if (col.field === 'open-policy') {
                return (
                    <Column
                        key={index}
                        body={renderOpenPolicyAction}
                        header={col.header}
                        style={{ textAlign: 'center' }}
                    />
                );
              }
              return (
                  <Column key={index} field={col.field} header={col.header} />
              );
            })}
          </DataTable>
        </div>
      </div>
  );
};

export default ApprovePolicyTable;